/*
 * @Author: Alex
 * @Date: 2023-05-17 16:05:35
 * @Description: canvas 绘制的指北针
 */
import { Color, asString } from 'ol/color'
import RenderEvent from 'ol/render/Event'
import { Stroke, Style } from 'ol/style'

import { CanvasControlBase } from './canvasControlBase'
import { ElementHelper } from '../mapUtil'

export default class CanvasCompassControl extends CanvasControlBase {
  private _img!: any
  constructor(options?: {
    className?: string
    style?: Style | Stroke
    rotateWithView?: boolean
    visible?: boolean
    image?: HTMLImageElement
    src?: 'compact' | 'default' | string
    widthPixel?: number
    heightPixel?: number
  }) {
    options = options || {}

    // Initialize parent
    const elt = document.createElement('div')
    elt.className =
      'ol-control ol-compassctrl ol-unselectable ol-hidden' +
      (options.className ? ' ' + options.className : '')
    elt.style.position = 'absolute'
    elt.style.visibility = 'hidden'
    if (options.heightPixel) {
      elt.style.height = `${options.heightPixel}px`
    }
    if (options.widthPixel) {
      elt.style.width = `${options.widthPixel}px`
    }

    let style =
      options.style instanceof Stroke
        ? new Style({ stroke: options.style })
        : options.style
    if (!options.style) {
      style = new Style({ stroke: new Stroke({ width: 0, color: '#fff' }) })
    }
    super({
      element: elt,
      style: style
    })

    this.set('rotateVithView', options.rotateWithView !== false)
    this.setVisible(options.visible !== false)

    this.setImage(options.image || options.src)
  }
  /** Set compass image
   * @param {Image|string} [img=default] the image or an url or 'compact' or 'default'
   */
  setImage(img: HTMLImageElement | string | undefined) {
    // The image
    if (img instanceof Image) {
      this._img = img
      this._img.onload = () => {
        if (this.getMap()) {
          try {
            this.getMap()?.renderSync()
          } catch (e) {
            /* ok */
          }
        }
      }
    } else if (typeof img === 'string') {
      // Load source
      switch (img) {
        case 'compact': {
          this._img = this.compactCompass_(
            this.element.clientWidth,
            asString(this.getStroke()?.getColor() as Color)
          )
          break
        }
        case 'default': {
          this._img = this.defaultCompass_(
            this.element.clientWidth,
            asString(this.getStroke()?.getColor() as Color)
          )
          break
        }
        default: {
          this._img = new Image()
          this._img.onload = () => {
            if (this.getMap()) {
              try {
                this.getMap()?.renderSync()
              } catch (e) {
                /* ok */
              }
            }
          }
          this._img.src = img
          break
        }
      }
    } else {
      this._img = this.defaultCompass_(
        this.element.clientWidth,
        asString(this.getStroke()?.getColor() as Color)
      )
    }
  }
  /** Create a default image.
   * @param {number} s the size of the compass
   * @private
   */
  private compactCompass_(s: number, color: string) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    s = canvas.width = canvas.height = s || 150
    const r = s / 2

    ctx.translate(r, r)
    ctx.fillStyle = color || '#963'
    ctx.lineWidth = 5
    ctx.lineJoin = ctx.lineCap = 'round'

    ctx.font = 'bold ' + r * 0.4 + 'px sans-serif'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'center'
    ctx.strokeStyle = '#fff'
    ctx.globalAlpha = 0.75
    ctx.strokeText('N', 0, -r / 2)
    ctx.globalAlpha = 1
    ctx.fillText('N', 0, -r / 2)
    ctx.beginPath()
    ctx.moveTo(0, r / 4)
    ctx.lineTo(r / 3, r / 2)
    ctx.lineTo(0, -r / 2)
    ctx.lineTo(-r / 3, r / 2)
    ctx.lineTo(0, r / 4)
    ctx.lineWidth = 12
    ctx.fillStyle = '#fff'
    ctx.globalAlpha = 0.75
    ctx.fill()
    ctx.stroke()
    ctx.globalAlpha = 1

    ctx.fillStyle = ctx.strokeStyle = color || '#963'
    ctx.lineWidth = 5
    ctx.beginPath()
    ctx.moveTo(0, r / 4)
    ctx.lineTo(0, -r / 2)
    ctx.lineTo(r / 3, r / 2)
    ctx.lineTo(0, r / 4)
    ctx.fill()
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(0, r / 4)
    ctx.lineTo(0, -r / 2)
    ctx.lineTo(-r / 3, r / 2)
    ctx.lineTo(0, r / 4)
    ctx.stroke()

    return canvas
  }
  /** Create a default image.
   * @param {number} s the size of the compass
   * @private
   */
  private defaultCompass_(s: number, color: string) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    s = canvas.width = canvas.height = s || 150
    const r = s / 2
    const r2 = 0.22 * r

    ctx.translate(r, r)
    ctx.strokeStyle = color || '#963'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.arc(0, 0, s * 0.41, 0, 2 * Math.PI)
    ctx.arc(0, 0, s * 0.44, 0, 2 * Math.PI)
    ctx.stroke()

    ctx.rotate(Math.PI / 4)
    this.drawCtx(ctx, r * 0.9, r2 * 0.8, color)
    this.drawCtx2(ctx, r * 0.9, r2 * 0.8)

    ctx.rotate(-Math.PI / 4)
    this.drawCtx(ctx, r, r2, color)
    this.drawCtx2(ctx, r, r2)

    return canvas
  }

  private drawCtx(
    ctx: CanvasRenderingContext2D,
    r: number,
    r2: number,
    color: string
  ) {
    ctx.fillStyle = color || '#963'
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(r, 0)
    ctx.lineTo(r2, r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(-r, 0)
    ctx.lineTo(-r2, -r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(0, r)
    ctx.lineTo(-r2, r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(0, -r)
    ctx.lineTo(r2, -r2)
    ctx.moveTo(0, 0)
    ctx.fill()
    ctx.stroke()
  }

  private drawCtx2(ctx: CanvasRenderingContext2D, r: number, r2: number) {
    ctx.globalCompositeOperation = 'destination-out'
    ctx.fillStyle = '#fff'
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(r, 0)
    ctx.lineTo(r2, -r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(-r, 0)
    ctx.lineTo(-r2, r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(0, r)
    ctx.lineTo(r2, r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(0, -r)
    ctx.lineTo(-r2, -r2)
    ctx.moveTo(0, 0)
    ctx.fill()
    ctx.globalCompositeOperation = 'source-over'
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(r, 0)
    ctx.lineTo(r2, -r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(-r, 0)
    ctx.lineTo(-r2, r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(0, r)
    ctx.lineTo(r2, r2)
    ctx.moveTo(0, 0)
    ctx.lineTo(0, -r)
    ctx.lineTo(-r2, -r2)
    ctx.moveTo(0, 0)
    ctx.stroke()
  }

  /** Get control visibility
   * @return {boolean}
   */
  getVisible() {
    return ElementHelper.getStyle(this.element, 'display') === 'block'
  }
  /** Set visibility
   * @param {boolean} b
   */
  setVisible(b: boolean) {
    if (b) this.element.classList.add('ol-visible')
    else this.element.classList.remove('ol-visible')
    if (this.getMap()) this.getMap()?.render()
  }
  /** Draw compass
   * @param {ol.event} e postcompose event
   * @private
   */
  protected draw(e: RenderEvent) {
    const ctx = this.getContext(e)
    if (!ctx || !this.getVisible()) return
    const canvas = ctx.canvas

    // 8 angles
    let i
    const da = []
    for (i = 0; i < 8; i++)
      da[i] = [Math.cos((Math.PI * i) / 8), Math.sin((Math.PI * i) / 8)]

    // Retina device
    const ratio = e.frameState?.pixelRatio ?? 1
    ctx.save()
    ctx.scale(ratio, ratio)

    const w = this.element.clientWidth
    const h = this.element.clientHeight
    const pos = { left: this.element.offsetLeft, top: this.element.offsetTop }

    const compass = this._img
    const rot = e.frameState?.viewState.rotation

    ctx.beginPath()
    ctx.translate(pos.left + w / 2, pos.top + h / 2)
    if (this.get('rotateVithView')) ctx.rotate(rot ?? 0)
    if (this.getStroke()?.getWidth()) {
      ctx.beginPath()
      ctx.strokeStyle = asString(this.getStroke()?.getColor() as Color)
      ctx.lineWidth = this.getStroke()?.getWidth() ?? 1
      const m = Math.max(canvas.width, canvas.height)
      for (i = 0; i < 8; i++) {
        ctx.moveTo(-da[i][0] * m, -da[i][1] * m)
        ctx.lineTo(da[i][0] * m, da[i][1] * m)
      }
      ctx.stroke()
    }

    if (compass.width) {
      ctx.drawImage(compass, -w / 2, -h / 2, w, h)
    }

    ctx.closePath()

    ctx.restore()
  }
}
